import { Component, AfterViewInit, OnInit } from '@angular/core';

import { MatCardModule } from '@angular/material/card';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DocumentRecord } from '../../services/documents/document.interface';
import { DocumentsService } from '../../services/documents/documents.service';

interface HrNode {
  key: string;
  label: string;
  /** Present on leaf nodes — the document subcategory this node drills down to. */
  subcategory?: string;
  /** Present on folder nodes — further nodes to drill into. */
  children?: HrNode[];
}

@Component({
    selector: 'app-human-resources',
    imports: [
    MatCardModule,
    RouterModule,
    MatIconModule,
    MatProgressSpinnerModule,
],
    templateUrl: './human-resources.component.html',
    styleUrl: './human-resources.component.css'
})
export class HumanResourcesComponent implements OnInit, AfterViewInit {

    payrollImageEnlarged = false;
    payrollImageLoading = true;
    payrollImageUrl: string | null = null;
    referralImageLoading = true;
    referralImageUrl: string | null = null;

    readonly hrTree: HrNode[] = [
      { key: 'general', label: 'General HR', subcategory: 'General' },
      { key: 'talent', label: 'Talent Acquisition', subcategory: 'Talent Acquisition' },
      { key: 'payroll', label: 'Payroll', subcategory: 'Payroll' },
      { key: 'benefits', label: 'Benefits', subcategory: 'Benefits' },
      { key: 'corrective', label: 'Corrective Action', subcategory: 'Corrective Actions' },
    ];

    formsLoading = true;
    allHrDocs: DocumentRecord[] = [];
    path: HrNode[] = [];

    constructor(private documentsService: DocumentsService, private sanitizer: DomSanitizer) {}

    ngOnInit() {
      this.loadPayrollImage();
      this.loadReferralImage();
      this.loadHrForms();
    }

    private loadHrForms() {
      this.formsLoading = true;
      this.documentsService.getDocuments('Human Resources').subscribe({
        next: (docs) => { this.allHrDocs = docs; this.formsLoading = false; },
        error: (err) => { console.error('Failed to load HR forms:', err); this.formsLoading = false; },
      });
    }

    private docsForSubcategory(subcategory: string): DocumentRecord[] {
      const docs = this.allHrDocs.filter((d) => (d.subcategory ?? '') === subcategory);
      if (subcategory === 'General' && this.policyManualDoc) {
        docs.push(this.policyManualDoc);
      }
      return docs;
    }

    nodeCount(node: HrNode): number {
      if (node.subcategory) {
        return this.docsForSubcategory(node.subcategory).length;
      }
      return (node.children ?? []).reduce((sum, child) => sum + this.nodeCount(child), 0);
    }

    countLabel(count: number): string {
      return count === 0 ? 'No documents' : `${count} document${count === 1 ? '' : 's'}`;
    }

    private nodeDocTitlesList(node: HrNode): string[] {
      if (node.subcategory) {
        return this.docsForSubcategory(node.subcategory).map((d) => d.title);
      }
      return (node.children ?? []).flatMap((child) => this.nodeDocTitlesList(child));
    }

    nodeDocTitles(node: HrNode): string {
      return this.nodeDocTitlesList(node).join(', ');
    }

    get currentNode(): HrNode | null {
      return this.path.length ? this.path[this.path.length - 1] : null;
    }

    get currentDocs(): DocumentRecord[] {
      const node = this.currentNode;
      if (!node || !node.subcategory) return [];
      return this.docsForSubcategory(node.subcategory);
    }

    get policyManualDoc(): DocumentRecord | undefined {
      return this.allHrDocs.find((d) => (d.subcategory ?? '') === 'Policy Manual');
    }

    private readonly docNotes: Record<string, string> = {
      'Direct Deposit': 'Please contact Maria Ulibarri at mulibarri@landpoint.net for guidance and to submit the completed form.',
      'W-4': 'Please contact Maria Ulibarri at mulibarri@landpoint.net for guidance and to submit the completed form.',
      '2026 Landpoint Benefits Guide': 'Please contact Tara Dobbins at tdobbins@landpoint.net for guidance.',
      'Empower Participant Enrollment': 'Please contact Maria Ulibarri at mulibarri@landpoint.net for guidance and to submit the completed form.',
      'Termination Form': 'Please contact hrsupport@landpoint.net for guidance and to submit the completed form.',
      'Cell Phone Reimbursement Request Form': 'Please contact hrsupport@landpoint.net for guidance and to submit the completed form.',
    };

    docNote(doc: DocumentRecord): string | undefined {
      return this.docNotes[doc.title] ?? (doc.description || undefined);
    }

    private readonly emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

    private escapeHtml(text: string): string {
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    linkifyEmails(text: string): SafeHtml {
      const linked = this.escapeHtml(text).replace(
        this.emailPattern,
        (email) => `<a href="mailto:${email}">${email}</a>`,
      );
      return this.sanitizer.bypassSecurityTrustHtml(linked);
    }

    enterNode(node: HrNode) {
      this.path = [...this.path, node];
    }

    goToCrumb(index: number) {
      this.path = this.path.slice(0, index + 1);
    }

    showCategories() {
      this.path = [];
    }

    openDocument(id: string) {
      this.documentsService.getSignedUrl(id).subscribe({
        next: ({ url }) => window.open(url, '_blank'),
        error: (err) => console.error('Failed to get signed URL:', err),
      });
    }

    private loadPayrollImage() {
      this.documentsService.getSingleDocument('Human Resources', 'Payroll Calendar').subscribe({
        next: (doc) => {
          if (!doc) { this.payrollImageLoading = false; return; }
          this.documentsService.getSignedUrl(doc._id).subscribe({
            next: ({ url }) => { this.payrollImageUrl = url; this.payrollImageLoading = false; },
            error: (err) => { console.error('Failed to resolve payroll calendar image:', err); this.payrollImageLoading = false; },
          });
        },
        error: (err) => { console.error('Failed to load payroll calendar image:', err); this.payrollImageLoading = false; },
      });
    }

    private loadReferralImage() {
      this.documentsService.getSingleDocument('Human Resources', 'Employee Referral').subscribe({
        next: (doc) => {
          if (!doc) { this.referralImageLoading = false; return; }
          this.documentsService.getSignedUrl(doc._id).subscribe({
            next: ({ url }) => { this.referralImageUrl = url; this.referralImageLoading = false; },
            error: (err) => { console.error('Failed to resolve employee referral flyer:', err); this.referralImageLoading = false; },
          });
        },
        error: (err) => { console.error('Failed to load employee referral flyer:', err); this.referralImageLoading = false; },
      });
    }

    ngAfterViewInit() {
        setTimeout(() => this.slightScrollOnLoad(), 200); // Delay ensures the page is fully rendered
    }

    private slightScrollOnLoad() {
      window.scrollBy({ top: 180, left: 0, behavior: 'smooth' }); // Adjust 'top' for a slight scroll
    }

    openPayrollImage() {
      this.payrollImageEnlarged = true;
    }

    closePayrollImage() {
      this.payrollImageEnlarged = false;
    }

}
